/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/utils/formatEventType.ts
 *
 * Rôle :
 *   LOT 7 (type d'événement configurable) : À partir du `type` de l'événement
 *   (colonne `tne_events.type`, ex. « salon », « seminaire »…), produire les
 *   FORMES FLÉCHIES FRANÇAISES du nom commun du type. Ces formes alimentent les
 *   tokens d'identité `{eventType*}` (cf. EventIdentityProvider) qui remplacent
 *   le mot « salon » codé en dur ~102 fois dans le catalogue i18n (fr.ts).
 *
 * Pourquoi plusieurs formes (et pas un simple « au {type} ») :
 *   Le français n'accorde pas « au/du/le » de façon uniforme selon le genre et
 *   le nombre du type. « au salon » marche pour salon/séminaire/symposium, mais
 *   il faut « aux portes ouvertes » et « à la biennale ». On pré-compose donc,
 *   par type, chaque forme utile, préposition + article INCLUS dans le token,
 *   pour que le catalogue n'ait plus qu'à écrire `{eventTypeAt} {eventName}`
 *   (« au salon NOLAS », « à la biennale … », « aux portes ouvertes … »).
 *
 * Formes exposées (toutes NON nulles) :
 *   - `bare`   : nom nu, minuscule, « salon », « portes ouvertes »
 *   - `cap`    : nom nu, Capitalisé, « Salon », « Portes ouvertes »
 *   - `def`    : article défini + nom, « le salon », « les portes ouvertes »
 *   - `defCap` : idem, Capitalisé, « Le salon », « Les portes ouvertes »
 *   - `at`     : « à » + article, « au salon », « aux portes ouvertes », « à la biennale »
 *   - `of`     : « de » + article, « du salon », « des portes ouvertes », « de la biennale »
 *
 * Réutilisable : 100 %, toute app DRANE / tout événement FR. Ajouter un type =
 *   une ligne dans {@link EVENT_TYPE_FORMS} + son option dans le back-office ;
 *   aucun ALTER TABLE (la colonne est un VARCHAR libre, cf. V82).
 *
 * Voir : hooks/useEventConfig.ts (source `type`),
 *        context/EventIdentityProvider.tsx (consommateur → tokens),
 *        app/docs/TYPES-EVENEMENTS-ET-MODULES.md (catalogue des types).
 *
 * @author Mikael
 * @since  2026-07-25 (LOT 7, type d'événement)
 */

/** Formes fléchies FR d'un type d'événement. Tous champs non nuls. */
export interface EventTypeForms {
  /** Nom nu, minuscule, « salon », « portes ouvertes ». */
  readonly bare: string;
  /** Nom nu, Capitalisé (début de phrase/label), « Salon », « Biennale ». */
  readonly cap: string;
  /** Article défini + nom, « le salon », « la biennale », « les portes ouvertes ». */
  readonly def: string;
  /** Idem, Capitalisé, « Le salon », « Les portes ouvertes ». */
  readonly defCap: string;
  /** « à » + article contracté, « au salon », « à la biennale », « aux portes ouvertes ». */
  readonly at: string;
  /** « de » + article contracté, « du salon », « de la biennale », « des portes ouvertes ». */
  readonly of: string;
}

/**
 * Slugs de type reconnus (whitelist applicative, miroir du commentaire de la
 * colonne `tne_events.type`, cf. V82). Un type inconnu retombe sur « salon ».
 */
export const EVENT_TYPE_SLUGS = [
  'salon',
  'portes-ouvertes',
  'seminaire',
  'symposium',
  'cycle-conferences',
  'biennale',
  'forum',
] as const;

export type EventTypeSlug = (typeof EVENT_TYPE_SLUGS)[number];

/** Type par défaut (rétrocompat NOLAS/CIRFA/TNE, cf. V82). */
export const DEFAULT_EVENT_TYPE: EventTypeSlug = 'salon';

/**
 * Table AUTORITAIRE des formes fléchies, une entrée par type. C'est le seul
 * endroit où vivent les accords FR : ajouter un type = ajouter une ligne ici
 * (et son option dans EventConfigPage). Les formes sont écrites À LA MAIN car
 * l'accord FR (genre + nombre + élision) n'est pas dérivable mécaniquement du
 * seul nom nu de façon fiable.
 */
export const EVENT_TYPE_FORMS: Readonly<Record<EventTypeSlug, EventTypeForms>> = {
  // masculin singulier, « le / au / du »
  salon: {
    bare: 'salon', cap: 'Salon',
    def: 'le salon', defCap: 'Le salon',
    at: 'au salon', of: 'du salon',
  },
  // féminin pluriel, « les / aux / des »
  'portes-ouvertes': {
    bare: 'portes ouvertes', cap: 'Portes ouvertes',
    def: 'les portes ouvertes', defCap: 'Les portes ouvertes',
    at: 'aux portes ouvertes', of: 'des portes ouvertes',
  },
  // masculin singulier
  seminaire: {
    bare: 'séminaire', cap: 'Séminaire',
    def: 'le séminaire', defCap: 'Le séminaire',
    at: 'au séminaire', of: 'du séminaire',
  },
  // masculin singulier
  symposium: {
    bare: 'symposium', cap: 'Symposium',
    def: 'le symposium', defCap: 'Le symposium',
    at: 'au symposium', of: 'du symposium',
  },
  // masculin singulier, nom composé « cycle de conférences »
  'cycle-conferences': {
    bare: 'cycle de conférences', cap: 'Cycle de conférences',
    def: 'le cycle de conférences', defCap: 'Le cycle de conférences',
    at: 'au cycle de conférences', of: 'du cycle de conférences',
  },
  // féminin singulier, « la / à la / de la »
  biennale: {
    bare: 'biennale', cap: 'Biennale',
    def: 'la biennale', defCap: 'La biennale',
    at: 'à la biennale', of: 'de la biennale',
  },
  // masculin singulier, « le / au / du ». Nom nu volontairement COURT
  // (« forum ») et non « forum des métiers » : le nom nu s'insère dans des
  // phrases où le complément serait redondant (« bienvenue au forum » suffit,
  // « bienvenue au forum des métiers des métiers de la Défense » non). Le
  // libellé long vit dans le catalogue de types (back-office / console).
  forum: {
    bare: 'forum', cap: 'Forum',
    def: 'le forum', defCap: 'Le forum',
    at: 'au forum', of: 'du forum',
  },
};

/**
 * Normalise une valeur de type brute (issue de la base) vers un slug connu.
 * Tolérant (offline-first §18) : casse/espaces ignorés, valeur inconnue ou
 * vide → `salon`. Ainsi une base sans la colonne (ou avec une coquille) rend
 * toujours des chaînes valides, jamais un token creux.
 *
 * @param raw Valeur brute de `tne_events.type` (peut être null/undefined).
 * @returns Un slug garanti présent dans {@link EVENT_TYPE_FORMS}.
 */
export function normalizeEventType(raw: string | null | undefined): EventTypeSlug {
  if (typeof raw !== 'string') return DEFAULT_EVENT_TYPE;
  const t = raw.trim().toLowerCase();
  return (EVENT_TYPE_SLUGS as readonly string[]).includes(t)
    ? (t as EventTypeSlug)
    : DEFAULT_EVENT_TYPE;
}

/**
 * Renvoie les formes fléchies FR pour un type d'événement donné. Point d'entrée
 * unique du helper, normalise puis lit la table.
 *
 * @param rawType Valeur brute de `tne_events.type`.
 * @returns Les 6 formes, toutes non nulles.
 *
 * @example formatEventTypeForms('salon').at    // 'au salon'
 * @example formatEventTypeForms('biennale').of // 'de la biennale'
 * @example formatEventTypeForms(null).bare     // 'salon' (repli)
 */
export function formatEventTypeForms(rawType: string | null | undefined): EventTypeForms {
  return EVENT_TYPE_FORMS[normalizeEventType(rawType)];
}
